// Managed hosting accounts (migration 20261005120000).
//
// One row per hosting customer. Separate from clients: client_id is optional.
// Websites are uptime-monitor rows (monitored_sites) carrying hosting_account_id,
// so a site added here is monitored by uptime-ping with no other wiring.

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseClient = SupabaseClient<any, 'public', any>;
const db = supabase as unknown as LooseClient;

export type HostingStatus = 'active' | 'paused' | 'cancelled';

export const HOSTING_STATUS_LABEL: Record<HostingStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  cancelled: 'Cancelled',
};

export type HostingAccount = {
  id: string;
  name: string;
  status: HostingStatus;
  client_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  provider: string | null;
  plan: string | null;
  monthly_price: number | null;
  started_on: string | null;
  notes: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  created_at: string;
  updated_at: string;
};

export type HostingAccountInput = Omit<HostingAccount, 'id' | 'created_at' | 'updated_at' | 'stripe_customer_id' | 'stripe_subscription_id'>;

export type HostedSite = {
  id: string;
  name: string;
  url: string;
  is_active: boolean | null;
  hosting_account_id: string | null;
  // Set by `hosting-runner.mjs link-cloudways`; a site with an app id can run the checklist.
  cloudways_server_id: string | null;
  cloudways_app_id: string | null;
  // A Netlify site id: the runner checks deploys, build, audit and headers instead of WordPress.
  netlify_site_id: string | null;
  hosting_skip_steps: string[] | null;
  /** marketing | app, decided by the setup job (managed-hosting skill); null = not set up yet. */
  site_kind: 'marketing' | 'app' | null;
  updated_at: string | null;
  /** When the runner last looked for this site's Cloudways app or Netlify site, and for which url (migration 20261103180000). */
  hosting_link_checked_at?: string | null;
  hosting_link_checked_url?: string | null;
};

/** A site the checklist runner can reach: it knows the site's Cloudways app or Netlify site. */
export const isRunnableSite = (s: HostedSite) => Boolean(s.cloudways_app_id || s.netlify_site_id);

/** Where the runner reaches a site, for labels; null when it can't. */
// Same precedence as the runner (scripts/hosting-runner/lib/run.mjs): a Cloudways app wins.
export const sitePlatform = (s: HostedSite | null | undefined): 'Netlify' | 'Cloudways' | null =>
  s?.cloudways_app_id && s?.cloudways_server_id ? 'Cloudways' : s?.netlify_site_id ? 'Netlify' : null;

/**
 * Where an unlinked site stands with the runner's auto-link (scripts/hosting-runner/lib/link.mjs):
 * `looking` until it has been checked at its current url since it was last touched (attaching
 * bumps updated_at), then `unmatched`. Null for a linked site.
 */
export function linkSearch(s: HostedSite): 'looking' | 'unmatched' | null {
  if (isRunnableSite(s)) return null;
  const checked = s.hosting_link_checked_at ? Date.parse(s.hosting_link_checked_at) : null;
  if (!checked || s.hosting_link_checked_url !== s.url) return 'looking';
  return s.updated_at && Date.parse(s.updated_at) > checked ? 'looking' : 'unmatched';
}

export async function fetchHostingAccounts(): Promise<HostingAccount[]> {
  const { data, error } = await db.from('hosting_accounts').select('*').order('name');
  if (error) throw error;
  return (data || []).map((r) => ({ ...r, monthly_price: r.monthly_price === null ? null : Number(r.monthly_price) }));
}

/** Every uptime-monitor site, attached or not, so the panel can offer the unattached ones. */
export async function fetchMonitoredSites(): Promise<HostedSite[]> {
  const cols = 'id, name, url, is_active, hosting_account_id, cloudways_server_id, cloudways_app_id, netlify_site_id, hosting_skip_steps, site_kind, updated_at';
  const { data, error } = await db.from('monitored_sites').select(`${cols}, hosting_link_checked_at, hosting_link_checked_url`).order('name');
  // 42703: the link-check columns aren't in this database yet; every unlinked site then reads as still being looked for.
  if (error?.code === '42703') {
    const again = await db.from('monitored_sites').select(cols).order('name');
    if (again.error) throw again.error;
    return again.data || [];
  }
  if (error) throw error;
  return data || [];
}

export async function saveHostingAccount(id: string | null, input: HostingAccountInput): Promise<HostingAccount> {
  const row = { ...input, updated_at: new Date().toISOString() };
  const query = id
    ? db.from('hosting_accounts').update(row).eq('id', id)
    : db.from('hosting_accounts').insert(row);
  const { data, error } = await query.select('*').single();
  if (error) throw error;
  return data as HostingAccount;
}

export async function deleteHostingAccount(id: string): Promise<void> {
  // Sites stay in the uptime monitor; the FK nulls their hosting_account_id.
  const { error } = await db.from('hosting_accounts').delete().eq('id', id);
  if (error) throw error;
}

/** Attach an existing uptime-monitor site to an account, or detach it (null). */
export async function setSiteAccount(siteId: string, accountId: string | null): Promise<void> {
  const { data, error } = await db
    .from('monitored_sites')
    .update({ hosting_account_id: accountId, updated_at: new Date().toISOString() })
    .eq('id', siteId)
    .select('id');
  if (error) throw error;
  // An RLS-blocked update returns no error and no rows.
  if (!data?.length) throw new Error('The site was not updated');
}

/** Add a new site to the uptime monitor, already attached to the account. */
export async function addHostedSite(accountId: string, name: string, rawUrl: string): Promise<void> {
  let url = rawUrl.trim();
  if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
  const { error } = await db.from('monitored_sites').insert({
    name: name.trim() || url.replace(/^https?:\/\//i, ''),
    url,
    is_active: true,
    alert_recipients: [],
    hosting_account_id: accountId,
  });
  if (error) throw error;
}

// ───────────────────────── Detail page ─────────────────────────

export async function fetchHostingAccount(id: string): Promise<HostingAccount | null> {
  const { data, error } = await db.from('hosting_accounts').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data ? { ...data, monthly_price: data.monthly_price === null ? null : Number(data.monthly_price) } : null;
}

export type UptimeDay = { site_id: string; day: string; checks: number; up: number; avg_ms: number | null };

/** Daily uptime per site, aggregated in Postgres (raw checks blow past the 1,000-row page). */
export async function fetchUptimeDaily(siteIds: string[], days: number): Promise<UptimeDay[]> {
  if (!siteIds.length) return [];
  const { data, error } = await db.rpc('hosting_uptime_daily', { p_site_ids: siteIds, p_days: days });
  if (error) throw error;
  return (data || []) as UptimeDay[];
}

export type PaymentStatus = 'current' | 'past_due' | 'unpaid' | 'failing' | 'canceled' | 'no_subscription' | 'unlinked';

export type HostingBilling = {
  linked: boolean;
  customerId?: string;
  currency?: string;
  lifetimeValue?: number;
  paymentCount?: number;
  firstPaymentAt?: string | null;
  lastPayment?: { amount: number; at: string } | null;
  lastFailure?: { amount: number; at: string; message: string | null } | null;
  paymentStatus: PaymentStatus;
  subscription?: { status: string; cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null } | null;
  openInvoices?: { count: number; amountDue: number; overdueCount: number };
  payments?: Array<{ id: string; amount: number; refunded: number; at: string; status: string; description: string | null }>;
};

/** Live Stripe facts for one account, via the hosting-billing edge function. */
export async function fetchHostingBilling(accountId: string): Promise<HostingBilling> {
  const { data, error } = await supabase.functions.invoke('hosting-billing', { body: { accountId } });
  if (error) {
    // FunctionsHttpError hides the body; surface the function's own message.
    const ctx = (error as { context?: Response }).context;
    const body = ctx && typeof ctx.json === 'function' ? await ctx.json().catch(() => null) : null;
    throw new Error(body?.error || error.message);
  }
  if (data?.error) throw new Error(data.error);
  return data as HostingBilling;
}

export type HostingCheck = { id: string; status: string; due_date: string | null; updated_at: string; assigned_to_name: string | null };

/**
 * The managed hosting check is one recurring "Managed Hosting" task that sweeps
 * every hosted site, so the last finished one is the last check for all of them.
 */
export async function fetchHostingChecks(): Promise<{ last: HostingCheck | null; next: HostingCheck | null }> {
  const cols = 'id, status, due_date, updated_at, assigned_to_name';
  const [done, upcoming] = await Promise.all([
    db
      .from('client_tasks')
      .select(cols)
      .ilike('title', 'managed hosting')
      .in('status', ['Submitted', 'Complete'])
      .order('updated_at', { ascending: false })
      .limit(1),
    db
      .from('client_tasks')
      .select(cols)
      .ilike('title', 'managed hosting')
      .in('status', ['Scheduled', 'In Progress', 'Blocked'])
      // Finished checks get archived; an archived open one is abandoned, not upcoming.
      .eq('archived', false)
      .order('due_date', { ascending: true })
      .limit(1),
  ]);
  if (done.error) throw done.error;
  if (upcoming.error) throw upcoming.error;
  return { last: (done.data?.[0] as HostingCheck) ?? null, next: (upcoming.data?.[0] as HostingCheck) ?? null };
}

// ───────────────────────── Checklist runs ─────────────────────────
// Migration 20261005180000. Run queues a hosting_runs row; the hosting runner
// claims it on the operations Mac and writes hosting_run_steps as it works.

export type HostingRunStatus = 'queued' | 'running' | 'passed' | 'attention' | 'failed' | 'cancelled';
export type HostingStepStatus = 'pending' | 'running' | 'passed' | 'attention' | 'failed' | 'skipped';

/** `updates: false` lists WordPress updates without applying them. */
export type HostingRunOptions = { updates?: boolean };

export type HostingRun = {
  id: string;
  hosting_account_id: string;
  status: HostingRunStatus;
  options: HostingRunOptions;
  requested_by: string | null;
  requested_by_name: string | null;
  runner: string | null;
  summary: string | null;
  error: string | null;
  created_at: string;
  started_at: string | null;
  heartbeat_at: string | null;
  finished_at: string | null;
  /** Set when someone has looked at an attention/failed run; the account then reads healthy until the next run. */
  reviewed_at: string | null;
  reviewed_by_name: string | null;
  review_note: string | null;
};

export type HostingRunStep = {
  id: string;
  run_id: string;
  /** Null for account-level steps (payment, uptime). */
  site_id: string | null;
  position: number;
  /**
   * payment | uptime | render | lighthouse, plus snapshot | updates | smtp | security (Cloudways)
   * or deploy | build | audit | versions | secrets | headers (Netlify).
   */
  step: string;
  title: string;
  status: HostingStepStatus;
  summary: string | null;
  /** Shape depends on `step`; written by the runner, so read it defensively. */
  detail: Record<string, unknown> | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
};

export const HOSTING_RUN_STATUS_LABEL: Record<HostingRunStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  passed: 'Passed',
  attention: 'Attention',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/** Queued or running: the unique index allows one of these per account. */
export const isLiveRun = (r: HostingRun | null | undefined) => r?.status === 'queued' || r?.status === 'running';

/** A run that needed attention or failed, and nobody has signed it off yet. */
export const canReviewRun = (r: HostingRun | null | undefined) => r?.status === 'attention' || r?.status === 'failed';
export const isReviewedRun = (r: HostingRun | null | undefined) => canReviewRun(r) && Boolean(r?.reviewed_at);

/** Sign off (or un-sign) a finished run. Goes through an RPC: RLS keeps finished runs read-only. */
export async function reviewHostingRun(id: string, reviewed: boolean, name: string | null, note?: string | null): Promise<void> {
  const { error } = await db.rpc('hosting_run_review', { p_run_id: id, p_reviewed: reviewed, p_name: name, p_note: note ?? null });
  if (error) throw error;
}

export async function queueHostingRun(accountId: string, options: { updates: boolean }, requestedByName: string | null): Promise<HostingRun> {
  const { data, error } = await db
    .from('hosting_runs')
    .insert({ hosting_account_id: accountId, options, requested_by_name: requestedByName })
    .select('*')
    .single();
  // uq_hosting_runs_live: a second click while one is queued or running.
  if (error?.code === '23505') throw new Error('A run is already queued or running for this account');
  if (error) throw error;
  return data as HostingRun;
}

/** Only a queued run can be cancelled; once the runner claims it, it finishes. */
export async function cancelHostingRun(id: string): Promise<void> {
  const { data, error } = await db.from('hosting_runs').update({ status: 'cancelled' }).eq('id', id).eq('status', 'queued').select('id');
  if (error) throw error;
  // The runner claimed it first, or RLS refused: no error, no rows.
  if (!data?.length) throw new Error('The run has already started, so it can no longer be cancelled');
}

export async function fetchHostingRuns(accountId: string, limit = 10): Promise<HostingRun[]> {
  const { data, error } = await db
    .from('hosting_runs')
    .select('*')
    .eq('hosting_account_id', accountId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as HostingRun[];
}

export async function fetchHostingRunSteps(runId: string): Promise<HostingRunStep[]> {
  const { data, error } = await db.from('hosting_run_steps').select('*').eq('run_id', runId).order('position');
  if (error) throw error;
  return (data || []) as HostingRunStep[];
}

/** Steps of several runs at once (the list page reads every account's latest). */
export async function fetchRunStepsFor(runIds: string[]): Promise<HostingRunStep[]> {
  if (!runIds.length) return [];
  const { data, error } = await db.from('hosting_run_steps').select('*').in('run_id', runIds).order('position');
  if (error) throw error;
  return (data || []) as HostingRunStep[];
}

/**
 * Latest run per account, for the list page. One query over the most recent
 * runs, reduced here; an account whose last run is older than the window
 * shows no last run, which at a few runs per account a month is years back.
 */
export async function fetchLatestRuns(): Promise<Record<string, HostingRun>> {
  const { data, error } = await db
    .from('hosting_runs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  const latest: Record<string, HostingRun> = {};
  for (const r of (data || []) as HostingRun[]) if (!latest[r.hosting_account_id]) latest[r.hosting_account_id] = r;
  return latest;
}
